import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client'

import { PmuActionLogStatus, PmuActionType } from '../graphqlTypes'
import {
   buildPullbackData,
   isMockNetworkError,
   isPullbackOperation
} from './pmuPullbackMock'

// Task 1 — Operation name set and matcher

const PMU_OPERATION_NAMES = [
   'InitiateTransferAction',
   'InitiateUpdateAction',
   'RetryFailedAction',
   'GetPMUApplicationVisibilityAndActions',
   'GetPMUApplicationLogs',
   'GetPMUApplicationLogDetail',
   'GetPMUApplicationLogStatuses'
] as const

type PMUOperationName = typeof PMU_OPERATION_NAMES[number]

const isPMUOperation = (name: string): name is PMUOperationName =>
   (PMU_OPERATION_NAMES as readonly string[]).includes(name)

// Local types mirror the generated mutation/query result shapes.
// They use the same enum values from graphqlTypes so schema drift in
// graphqlTypes.ts (regenerated from .graphql files) will break tsc here.

interface InitiatePMUActionResultBranch {
   __typename?: 'InitiatePMUActionResult'
   pmuActionLogId: string
}

interface RetryFailedActionResultBranch {
   __typename?: 'RetryFailedActionResult'
   pmuActionLogId: string
}

interface PMUFieldResponseShape {
   __typename?: 'PMUFieldResponseType'
   fieldId: string
   gofResponseId?: string | null
   response?: string | null
}

interface TabFieldResponsesShape {
   __typename?: 'TabFieldResponsesType'
   visibleFieldResponses: PMUFieldResponseShape[]
}

interface ApplicationViewTypeShape {
   __typename?: 'ApplicationViewType'
   permittedActions: PmuActionType[]
   tabFieldResponses: TabFieldResponsesShape[]
}

interface PMUActionLogTypeShape {
   __typename?: 'PMUActionLogType'
   pmuActionLogId: string
   actionType: PmuActionType
   applicationId: string
   applicationNumber: string
   status: PmuActionLogStatus
   reason: string
   initiatorUserId: string
   startedAt: string
   completedAt?: string | null
}

interface GetPMUActionLogsResultShape {
   __typename?: 'GetPMUActionLogsResult'
   totalCount: number
   logs: PMUActionLogTypeShape[]
}

interface PMUFieldDiffTypeShape {
   __typename?: 'PMUFieldDiffType'
   fieldId: string
   fieldLabel?: string | null
   gofResponseId?: string | null
   gofName?: string | null
   oldValue?: string | null
   newValue?: string | null
   kind?: string | null
}

interface PMUDocumentTypeShape {
   __typename?: 'PMUDocumentType'
   s3Key: string
   fileName: string
   contentType: string
   presignedUrl?: string | null
}

interface PMUActionLogDetailTypeShape {
   __typename?: 'PMUActionLogDetailType'
   pmuActionLogId: string
   actionType: PmuActionType
   applicationId: string
   applicationNumber: string
   leadDistributionLogId?: string | null
   pipelineItemTemplateId: string
   status: PmuActionLogStatus
   reason: string
   initiatorUserId: string
   startedAt: string
   completedAt?: string | null
   updatedAt?: string | null
   errorMessage?: string | null
   fieldDiffs: PMUFieldDiffTypeShape[]
   supportingDocuments: PMUDocumentTypeShape[]
}

interface PMUActionLogStatusTypeShape {
   __typename?: 'PMUActionLogStatusType'
   pmuActionLogId: string
   status: PmuActionLogStatus
}

interface GetPMUActionLogStatusesResultShape {
   __typename?: 'GetPMUActionLogStatusesResult'
   logStatuses: PMUActionLogStatusTypeShape[]
}

// Task 3 — Mutation fixtures

const initiateTransferActionFixture = {
   __typename: 'Mutation' as const,
   initiateTransferAction: {
      __typename: 'InitiatePMUActionResult',
      pmuActionLogId: 'mock-transfer-log-001'
   } as InitiatePMUActionResultBranch
}

const initiateUpdateActionFixture = {
   __typename: 'Mutation' as const,
   initiateUpdateAction: {
      __typename: 'InitiatePMUActionResult',
      pmuActionLogId: 'mock-update-log-001'
   } as InitiatePMUActionResultBranch
}

const retryFailedActionFixture = {
   __typename: 'Mutation' as const,
   retryFailedAction: {
      __typename: 'RetryFailedActionResult',
      pmuActionLogId: 'mock-retry-log-001'
   } as RetryFailedActionResultBranch
}

// Task 4 — Visibility & actions fixture

const visibilityAndActionsFixture: {
   __typename?: 'Query'
   resolveApplicationView: ApplicationViewTypeShape
} = {
   __typename: 'Query',
   resolveApplicationView: {
      __typename: 'ApplicationViewType',
      permittedActions: [PmuActionType.Transfer, PmuActionType.Update],
      tabFieldResponses: [
         {
            __typename: 'TabFieldResponsesType',
            visibleFieldResponses: [
               {
                  __typename: 'PMUFieldResponseType',
                  fieldId: 'mock-field-001',
                  gofResponseId: 'mock-gof-response-001',
                  response: 'Mock Field Value A'
               },
               {
                  __typename: 'PMUFieldResponseType',
                  fieldId: 'mock-field-002',
                  gofResponseId: 'mock-gof-response-002',
                  response: 'Mock Field Value B'
               }
            ]
         },
         {
            __typename: 'TabFieldResponsesType',
            visibleFieldResponses: [
               {
                  __typename: 'PMUFieldResponseType',
                  fieldId: 'mock-field-003',
                  gofResponseId: null,
                  response: 'Mock Field Value C'
               }
            ]
         }
      ]
   }
}

// Task 5 — Logs, log detail, and log statuses fixtures

const logsFixture: {
   __typename?: 'Query'
   getPmuActionLogs: GetPMUActionLogsResultShape
} = {
   __typename: 'Query',
   getPmuActionLogs: {
      __typename: 'GetPMUActionLogsResult',
      totalCount: 3,
      logs: [
         {
            __typename: 'PMUActionLogType',
            pmuActionLogId: 'mock-log-001',
            actionType: PmuActionType.Transfer,
            applicationId: 'mock-app-001',
            applicationNumber: 'APP-2024-001',
            status: PmuActionLogStatus.Completed,
            reason: 'Transfer to new pipeline stage',
            initiatorUserId: 'mock-user-001',
            startedAt: '2024-04-01T10:00:00.000Z',
            completedAt: '2024-04-01T10:01:00.000Z'
         },
         {
            __typename: 'PMUActionLogType',
            pmuActionLogId: 'mock-log-002',
            actionType: PmuActionType.Update,
            applicationId: 'mock-app-001',
            applicationNumber: 'APP-2024-001',
            status: PmuActionLogStatus.Failed,
            reason: 'Batch field update request',
            initiatorUserId: 'mock-user-002',
            startedAt: '2024-04-02T09:00:00.000Z',
            completedAt: '2024-04-02T09:01:30.000Z'
         },
         {
            __typename: 'PMUActionLogType',
            pmuActionLogId: 'mock-log-003',
            actionType: PmuActionType.Transfer,
            applicationId: 'mock-app-001',
            applicationNumber: 'APP-2024-001',
            status: PmuActionLogStatus.InProgress,
            reason: 'Retry after partial success',
            initiatorUserId: 'mock-user-001',
            startedAt: '2024-04-03T11:30:00.000Z',
            completedAt: null
         }
      ]
   }
}

const logDetailFixture: {
   __typename?: 'Query'
   getPmuActionLogDetail: PMUActionLogDetailTypeShape
} = {
   __typename: 'Query',
   getPmuActionLogDetail: {
      __typename: 'PMUActionLogDetailType',
      pmuActionLogId: 'mock-log-001',
      actionType: PmuActionType.Transfer,
      applicationId: 'mock-app-001',
      applicationNumber: 'APP-2024-001',
      leadDistributionLogId: 'mock-ldl-001',
      pipelineItemTemplateId: 'mock-template-001',
      status: PmuActionLogStatus.Completed,
      reason: 'Transfer to new pipeline stage',
      initiatorUserId: 'mock-user-001',
      startedAt: '2024-04-01T10:00:00.000Z',
      completedAt: '2024-04-01T10:01:00.000Z',
      updatedAt: '2024-04-01T10:01:00.000Z',
      errorMessage: null,
      fieldDiffs: [
         {
            __typename: 'PMUFieldDiffType',
            fieldId: 'mock-field-001',
            fieldLabel: 'Applicant Name',
            gofResponseId: 'mock-gof-response-001',
            gofName: 'Personal Details',
            oldValue: 'Jane Doe',
            newValue: 'Jane Smith',
            kind: 'UPDATED'
         },
         {
            __typename: 'PMUFieldDiffType',
            fieldId: 'mock-field-002',
            fieldLabel: 'Phone Number',
            gofResponseId: 'mock-gof-response-002',
            gofName: 'Contact Info',
            oldValue: '+91-9000000000',
            newValue: '+91-9000000001',
            kind: 'UPDATED'
         }
      ],
      supportingDocuments: [
         {
            __typename: 'PMUDocumentType',
            s3Key: 'mock-docs/supporting-doc-001.pdf',
            fileName: 'supporting-doc-001.pdf',
            contentType: 'application/pdf',
            presignedUrl: 'https://mock-s3.example.com/supporting-doc-001.pdf'
         }
      ]
   }
}

const logStatusesFixture: {
   __typename?: 'Query'
   getPmuActionLogStatuses: GetPMUActionLogStatusesResultShape
} = {
   __typename: 'Query',
   getPmuActionLogStatuses: {
      __typename: 'GetPMUActionLogStatusesResult',
      logStatuses: [
         {
            __typename: 'PMUActionLogStatusType',
            pmuActionLogId: 'mock-log-001',
            status: PmuActionLogStatus.Completed
         },
         {
            __typename: 'PMUActionLogStatusType',
            pmuActionLogId: 'mock-log-002',
            status: PmuActionLogStatus.Failed
         },
         {
            __typename: 'PMUActionLogStatusType',
            pmuActionLogId: 'mock-log-003',
            status: PmuActionLogStatus.InProgress
         }
      ]
   }
}

// Task 1 — Fixture map keyed by operation name

type FixtureData =
   | typeof initiateTransferActionFixture
   | typeof initiateUpdateActionFixture
   | typeof retryFailedActionFixture
   | typeof visibilityAndActionsFixture
   | typeof logsFixture
   | typeof logDetailFixture
   | typeof logStatusesFixture

type FixtureMap = Record<PMUOperationName, FixtureData>

const FIXTURES: FixtureMap = {
   InitiateTransferAction: initiateTransferActionFixture,
   InitiateUpdateAction: initiateUpdateActionFixture,
   RetryFailedAction: retryFailedActionFixture,
   GetPMUApplicationVisibilityAndActions: visibilityAndActionsFixture,
   GetPMUApplicationLogs: logsFixture,
   GetPMUApplicationLogDetail: logDetailFixture,
   GetPMUApplicationLogStatuses: logStatusesFixture
}

// Task 2 — Structured console logging

const logInterceptedOperation = (
   operationName: string,
   variables: Record<string, unknown>,
   data: unknown
): void => {
   console.group(`[PMU-MOCK] ${operationName}`)
   console.log('variables', variables)
   console.log('response', data)
   console.log('timestamp', new Date().toISOString())
   console.groupEnd()
}

// Task 1 — Link factory

export const createPMUMockLink = (): ApolloLink =>
   new ApolloLink((operation: Operation, forward) => {
      const { operationName, variables } = operation

      if (isPullbackOperation(operationName)) {
         return new Observable<FetchResult>(observer => {
            try {
               const data = buildPullbackData(operationName, variables)
               logInterceptedOperation(operationName, variables, data)
               observer.next({ data })
               observer.complete()
            } catch (error) {
               if (!isMockNetworkError(error)) throw error
               observer.error(error)
            }
         })
      }

      if (!isPMUOperation(operationName)) {
         return forward(operation)
      }

      const data = FIXTURES[operationName]

      return new Observable<FetchResult>(observer => {
         logInterceptedOperation(operationName, variables, data)
         observer.next({ data })
         observer.complete()
      })
   })
